<template>
  <div class="edit-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          :to="{ name: 'ComicDetail', params: { id: $route.params.id } }"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Edit Comic</h1>
          <p class="text-caption text-medium-emphasis mb-0">Update comic details in your collection</p>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!loading && !loadError" class="d-flex align-center flex-wrap ga-2">
        <v-btn
          :to="{ name: 'ComicDetail', params: { id: $route.params.id } }"
          variant="tonal"
          class="btn-modern"
        >
          Cancel
        </v-btn>

        <v-btn
          color="#f59e0b"
          class="btn-modern text-white font-weight-bold"
          :disabled="saving"
          :loading="saving"
          prepend-icon="mdi-content-save-check-outline"
          @click="saveComic"
        >
          Save Changes
        </v-btn>
      </div>
    </div>

    <!-- Loading / Error States -->
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="#f59e0b" size="56" width="4" />
      <div class="text-body-2 text-medium-emphasis mt-4">Loading comic data...</div>
    </div>

    <v-alert v-else-if="loadError" type="error" class="mb-4" variant="tonal">{{ loadError }}</v-alert>

    <template v-else>
      <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
        {{ error }}
      </v-alert>

      <!-- Main Form -->
      <v-form ref="formRef">
        <v-row>
          <!-- Left Column: Cover Preview -->
          <v-col cols="12" md="4" lg="3">
            <v-card class="glass-card pa-4 text-center sticky-poster-card">
              <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Comic Cover</div>

              <div class="poster-dropzone mb-3">
                <v-img
                  v-if="posterPreview"
                  :src="posterPreview"
                  :aspect-ratio="2 / 3"
                  cover
                  class="rounded-lg shadow-md"
                />
                <div v-else class="poster-empty-placeholder">
                  <v-icon size="48" color="#f59e0b" class="mb-2">mdi-draw</v-icon>
                  <div class="text-caption font-weight-bold">No Cover Image</div>
                </div>
              </div>

              <v-file-input
                v-model="imageFile"
                label="Replace Cover Image"
                accept="image/*"
                density="compact"
                hide-details
                prepend-icon=""
                prepend-inner-icon="mdi-upload"
                variant="outlined"
                class="mb-2"
              />

              <v-text-field
                v-model="comic.imageurl"
                label="Or Cover URL"
                density="compact"
                hide-details
                variant="outlined"
              />
            </v-card>
          </v-col>

          <!-- Right Column: Sections -->
          <v-col cols="12" md="8" lg="9">
            <!-- Section 1: Details -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="#f59e0b" size="22">mdi-information-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Comic Details</h3>
              </div>

              <v-row dense>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="comic.title"
                    label="Comic Title *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="comic.category"
                    :items="CATEGORIES"
                    label="Category *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="comic.author"
                    label="Author / Writer"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="comic.artist"
                    label="Illustrator / Artist"
                    density="comfortable"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="comic.serie"
                    label="Series Name"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="comic.tome"
                    label="Tome / Issue #"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="comic.year"
                    label="Release Year"
                    density="comfortable"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="comic.publisher"
                    label="Publisher"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-select
                    v-model="comic.language"
                    :items="LANGUAGES"
                    label="Language"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="comic.pageCount"
                    label="Pages"
                    density="comfortable"
                  />
                </v-col>

                <!-- Star Rating -->
                <v-col cols="12" sm="9" class="d-flex align-center ga-3 mt-2">
                  <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                  <v-rating
                    v-model="ratingNumber"
                    half-increments
                    density="comfortable"
                    color="amber"
                    active-color="amber"
                  />
                  <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
                </v-col>
              </v-row>
            </v-card>

            <!-- Section 2: Description -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="#f59e0b" size="22">mdi-text-box-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Summary & Description</h3>
              </div>

              <v-textarea
                v-model="comic.description"
                label="Comic Synopsis / Overview"
                rows="5"
                auto-grow
                density="comfortable"
                variant="outlined"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CATEGORIES, LANGUAGES } from '@/config/comics'
import ComicsAPI from '@/services/comics'
import { appendComicFormData, defaultComicForm } from '@/utils/comicForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'EditComicPost',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref(null)
    const comic = reactive(defaultComicForm())
    const oldImage = ref('')
    const imageFile = ref(null)
    const loading = ref(true)
    const saving = ref(false)
    const error = ref('')
    const loadError = ref('')

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(comic.rating) || 0,
      set: (value) => {
        comic.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      const file = imageFile.value
      if (file) {
        const selected = Array.isArray(file) ? file[0] : file
        if (selected) return URL.createObjectURL(selected)
      }
      if (comic.imageurl) return comic.imageurl
      if (comic.image) return `/uploads/${comic.image}`
      return ''
    })

    const load = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const { data } = await ComicsAPI.getPostByID(route.params.id)
        Object.assign(comic, { ...defaultComicForm(), ...data })
        oldImage.value = data.image || ''
      } catch (err) {
        loadError.value = apiErrorMessage(err, 'Comic not found')
      } finally {
        loading.value = false
      }
    }

    const saveComic = async () => {
      error.value = ''
      const title = String(comic.title || '').trim()
      const category = String(comic.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      comic.title = title
      comic.category = category
      saving.value = true
      try {
        const formData = new FormData()
        appendComicFormData(formData, comic)
        formData.append('old_image', oldImage.value || '')
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await ComicsAPI.updatePost(route.params.id, formData)
        router.push({ name: 'ComicDetail', params: { id: route.params.id } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to update comic')
      } finally {
        saving.value = false
      }
    }

    onMounted(load)

    return {
      formRef,
      comic,
      imageFile,
      loading,
      saving,
      error,
      loadError,
      CATEGORIES,
      LANGUAGES,
      required,
      ratingNumber,
      posterPreview,
      saveComic,
    }
  },
}
</script>

<style scoped>
.edit-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}
</style>

